import { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Mail, ArrowRight, Shield, HelpCircle } from 'lucide-react';
import { fbTrack } from '../lib/consent';

const MerciAchatPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const orderId = searchParams.get('order_id') || searchParams.get('txn_id') || '';
  const amount = parseFloat(searchParams.get('amount') || '0') || null;

  useEffect(() => {
    window.scrollTo(0, 0);
    // FB Pixel — Purchase (dédupliqué avec la CAPI serveur via eventID stable)
    // Si order_id n'est pas fourni dans l'URL, on fallback sur l'email pour la dédup
    const eventId = `purchase-${orderId || email}`;
    fbTrack('Purchase', {
      currency: 'EUR',
      value: amount || 0,
      content_name: 'Pack Auto-Entrepreneur',
    }, eventId);
  }, [email, orderId, amount]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <Card className="bg-white/10 backdrop-blur-lg border-emerald-500/30 text-white overflow-hidden">
          <CardContent className="p-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Merci pour votre achat !</h1>
              <p className="text-emerald-100 text-lg">Votre paiement a bien été reçu</p>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Email notification */}
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Vérifiez votre boîte email
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Nous venons de vous envoyer un email contenant :
                    </p>
                    <ul className="text-gray-300 space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        Votre identifiant de connexion (email)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        Votre mot de passe temporaire
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        Le lien pour accéder à votre Dashboard
                      </li>
                    </ul>
                    {email && (
                      <p className="text-emerald-400 font-medium mt-4">
                        📧 Envoyé à : {email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Spam warning */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                <p className="text-amber-200 text-sm">
                  <strong>💡 Conseil :</strong> Si vous ne trouvez pas l'email, vérifiez vos <strong>spams</strong> ou <strong>promotions</strong>. 
                  L'email provient de <strong>contact@capsulepatrimoine.com</strong>
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <Link to="/login" className="block">
                  <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-6 text-lg font-semibold">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Aller à la page de connexion
                  </Button>
                </Link>

                <p className="text-center text-gray-400 text-sm">
                  Utilisez les identifiants reçus par email pour vous connecter
                </p>
              </div>

              {/* Guarantee */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex items-center justify-center gap-3 text-gray-400">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm">Garantie satisfait ou remboursé 14 jours</span>
                </div>
              </div>

              {/* Help */}
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  <HelpCircle className="w-4 h-4 inline mr-1" />
                  Un problème ? Contactez-nous : {' '}
                  <a href="mailto:contact@capsulepatrimoine.com" className="text-emerald-400 hover:underline">
                    contact@capsulepatrimoine.com
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © 2026 Capsule Patrimoine
        </p>
      </div>
    </div>
  );
};

export default MerciAchatPage;
